import { type FC } from "react";
import { generatePath } from "react-router-dom";

import { Group, Paper, Stack, Text } from "@mantine/core";
import { useInputState } from "@mantine/hooks";
import { Spacing } from "@snapchat/snap-design-system";
import { FolderIcon, Sizes } from "@snapchat/snap-design-system-icons";

import { CardContent, EmptyFeaturePage } from "../../../core/components";
import {
    BusinessManagerLinkText,
    DefaultMembershipMessage,
    LensFoldersNotAvailalble,
} from "../../../core/components/messages.generated";
import { AbsoluteRoute } from "../../../core/routes";
import { ContainerKind, MyLensesRoleType } from "../../../gql-generated/types";
import { Flexbox } from "../../../shared/components";
import { LensAutomationToolAlert } from "../../../shared/components/LensAutomationToolAlert";
import { BUSINESS_MANAGER_URL } from "../../../shared/constants";
import {
    useSelectedLensCentralOrganizationNotIncludesCamkitOrgs,
    useSelectedOrganizationContainer,
} from "../../../shared/hooks";
import { makeContainerKey, useRolesStore } from "../../../shared/hooks/roles";
import { Feature } from "../../../shared/models";
import { LinkStyled } from "../../../shared/styles";
import { NoLensFoldersCard } from "../no-lens-folders/NoLensFoldersCard";

import { folderIconContainer, folderSubTitle, folderTitle, paper } from "./LensFoldersList.module.css";
import { LensFoldersPageHeader } from "./header";

const makeRoleType = (rolesList: MyLensesRoleType[] = []): string => {
    if (rolesList.includes(MyLensesRoleType.RoleTypeLensFolderAdmin)) {
        return "Admin";
    } else if (rolesList.includes(MyLensesRoleType.RoleTypeLensFolderManager)) {
        return "Manager";
    } else if (rolesList.includes(MyLensesRoleType.RoleTypeLensFolderCreator)) {
        return "Creator";
    }
    return "";
};

const hasFeatures = (
    container: ReturnType<typeof useSelectedOrganizationContainer>["container"],
): container is Extract<typeof container, { features: unknown }> => {
    return container !== undefined && "features" in container;
};

export const LensFoldersList: FC = () => {
    const selectedOrganizationV3 = useSelectedLensCentralOrganizationNotIncludesCamkitOrgs();
    // TODO(@kkold): improve - add loading state
    const { container } = useSelectedOrganizationContainer();

    const lensFoldersList = selectedOrganizationV3?.lensSpacesList ?? [];
    const { rolesStore } = useRolesStore();
    const [searchText, setSearchText] = useInputState("");

    const lensFoldersListFiltered = lensFoldersList.filter((lensFolder) => {
        // TODO - https://en.wikipedia.org/wiki/Levenshtein_distance
        const searchTextLower = searchText.toLocaleLowerCase();
        const foldersListFiltered = lensFolder.name.toLocaleLowerCase().includes(searchTextLower);
        return foldersListFiltered;
    });

    if (hasFeatures(container) && !container.features.has(Feature.lensFolders)) {
        return (
            <EmptyFeaturePage title={<LensFoldersNotAvailalble />}>
                <CardContent data-test-id="lens-folders-no-auth-content">
                    <DefaultMembershipMessage
                        link={
                            <a
                                href={BUSINESS_MANAGER_URL}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="lens-folders-no-auth-bm-btn"
                            >
                                <BusinessManagerLinkText />
                            </a>
                        }
                    />
                </CardContent>
            </EmptyFeaturePage>
        );
    }

    return (
        <Stack data-testid="lens-folders-grid">
            <Flexbox flexDirection={"column"}>
                <LensAutomationToolAlert />
                <LensFoldersPageHeader searchText={searchText} setSearchText={setSearchText} />
            </Flexbox>
            {lensFoldersListFiltered.length > 0 ? (
                <Stack mb={20} gap={0}>
                    {lensFoldersListFiltered.map((lensFolder) => {
                        const rolesList = rolesStore?.[
                            makeContainerKey(lensFolder.id, ContainerKind.ContainerKindLensFolder)
                        ]?.map(({ roleType }) => roleType);

                        const route = generatePath(AbsoluteRoute.ORG_LENS_FOLDER_LENSES, {
                            orgId: lensFolder.organizationId,
                            lensFolderId: lensFolder.id,
                        });

                        return (
                            <LinkStyled to={route} key={lensFolder.id}>
                                <Paper p={Spacing.XS} radius={0} data-testid="lens-folders-grid-card" className={paper}>
                                    <Group>
                                        <div className={folderIconContainer}>
                                            <FolderIcon size={Sizes.Large} />
                                        </div>
                                        <Stack gap="xs">
                                            <Text component="span" className={`paragraph-p200 ${folderTitle}`}>
                                                {lensFolder.name}
                                            </Text>
                                            <Text
                                                component="span"
                                                className={`paragraph-p300 ${folderSubTitle}`}
                                                c="dimmed"
                                            >
                                                {makeRoleType(rolesList)}
                                            </Text>
                                        </Stack>
                                    </Group>
                                </Paper>
                            </LinkStyled>
                        );
                    })}
                </Stack>
            ) : (
                <NoLensFoldersCard fromSearch={lensFoldersList.length > 0} />
            )}
        </Stack>
    );
};
